import mustache from "mustache";
import { BaseCreateRender } from "../../../../base/BaseCreateRender";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import { ConfirmModalRender } from "../../../../implementations/modal/ConfirmModalRender";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import { ClienteFincaBaseResponse } from "../../../../../modulos/terceros/response/ClienteFincaBaseResponse";
import {
  fx_createRegistroFincaBorradorParaCompletar,
  fx_createRegistroFincaBorradorVacio,
  fx_pasoParaRetomar,
  RegistroFincaBorrador,
  RegistroTemporalLeido,
} from "../../../../../modulos/terceros/wizard/response/RegistroFincaBorrador";
import { parseWizardErrorMessage } from "../../../../../modulos/terceros/wizard/response/WizardValidationErrorResponse";
import { registroFincaTempGrpcService } from "../../../../../modulos/terceros/wizard/services/RegistroFincaTempGrpcService";
import { PoligonoConNombre } from "../map/depurarPoligonos";
import {
  BaseRegistroFincaPasoRender,
  RegistroFincaFooterOption,
  RegistroFincaFooterOptions,
} from "./BaseRegistroFincaPasoRender";
import { RegistroFincaPaso1Render } from "./RegistroFincaPaso1Render";
import { RegistroFincaPaso2Render } from "./RegistroFincaPaso2Render";
import { RegistroFincaPaso3Render } from "./RegistroFincaPaso3Render";
import { RegistroFincaPaso4Render } from "./RegistroFincaPaso4Render";
import { RegistroFincaPaso5Render } from "./RegistroFincaPaso5Render";
import { RegistroFincaPaso6Render } from "./RegistroFincaPaso6Render";
import template from "./registroFincaModalTemplate.html";

mustache.parse(template);

const TOTAL_PASOS = 6;

/** Modal full-screen del wizard de registro de finca. Implementación propia
 *  (NO extiende `BaseFullScreenModalRender`): el body no es scrollable
 *  (`overflow: hidden` — el paso 3 es un mapa, necesita medidas exactas) y
 *  **no hay footer**: los 2 botones de avance (Volver/Continuar) viven en la
 *  BARRA SUPERIOR, a la derecha (antes eran una barra al pie — se movieron
 *  para que el body se quede con todo el alto, ver `.registro-finca-bar` en
 *  `crearFincaPage.css`). Siguen siendo **fijos** en el propio template
 *  (`registroFincaModalTemplate.html`) — cada paso
 *  solo dice cuáles usar y con qué label/estado (`getFooterOptions`, nombre
 *  histórico de cuando eran del footer, ver
 *  `BaseRegistroFincaPasoRender`), el modal los muestra/oculta y recablea
 *  (`.onclick`, sin acumular listeners al reusar los mismos botones entre
 *  pasos). Había un tercer botón ("Omitir") — se sacó definitivamente: con
 *  el borrador en memoria, el botón "×" de cerrar ya hacía exactamente lo
 *  mismo (perder todo el progreso), quedaba redundante. El botón de cerrar
 *  (×, a la derecha de la barra) confirma antes de cerrar
 *  (`ConfirmModalRender`) en vez de cerrar directo. z-index apilado
 *  vía `modalZIndexService` (heredado de `BaseCreateRender`, igual que el
 *  resto de los modales). La barra superior también muestra el avance
 *  ("Paso X/6") y, si el paso actual lo declara (`getInstructions`), un botón
 *  de ayuda "?" que abre un panel de indicaciones superpuesto al body (no
 *  empuja el layout del paso).
 *
 *  **Guardado progresivo en un registro temporal.** Cada paso confirma su
 *  parte en el esquema `registro_finca` del servidor
 *  (`RegistroFincaTempGrpcService`); nada llega a las fincas reales hasta
 *  que el paso 6 finaliza con el registro completo. El borrador
 *  (`RegistroFincaBorrador`) solo lleva la IDENTIDAD del registro (id,
 *  nombre, tipo, fuente, último paso): el área y los lotes no viajan en
 *  memoria entre pasos, cada paso los lee de la base al montarse. Al
 *  abrir, el modal pide el registro en curso del cliente y, si existe, lo
 *  retoma en el paso pendiente (`fx_pasoParaRetomar`); cerrar el modal no
 *  pierde nada de lo confirmado. Un cliente tiene como máximo un registro en
 *  curso: mientras exista, se retoma ese aunque se haya pedido otra cosa. Al
 *  terminar paso 6 con éxito, `_onFincaCreated` avisa al llamador y el modal
 *  se cierra solo (`dispose()`).
 *
 *  **Dos modos:** alta normal (crea una finca nueva, paso 1 a 6 completo) o
 *  completar una finca EXISTENTE sin AOI/lotes (típicamente creada desde el
 *  panel administrativo "Plataforma", `_fincaACompletar` no nulo) — en ese
 *  caso el borrador arranca con nombre/tipo ya conocidos y el wizard salta
 *  directo a paso 2 (nada que pedir/validar en paso 1), y paso 6 termina
 *  completando esa finca en vez de crear una nueva (ver
 *  `RegistroFincaPaso6Render`). El wizard nunca "edita" en el sentido de
 *  modificar una finca ya completa — solo llena lo que le falta a una que
 *  todavía no tiene AOI/lotes. */
export class RegistroFincaModalRender extends BaseCreateRender {
  protected readonly elementId = "registroFincaModal";

  /** @param _maximoHectareas Máximo de hectáreas del plan, o `null` si el
   *   plan es demo (sin límite) — lo usará el paso 3 al reconstruirse.
   *  @param _onFincaCreated Notifica la finca creada/completada al terminar
   *   el paso 6 — el llamador (`ClienteAsideMenuRender`) refresca su lista
   *   de fincas y selecciona la nueva.
   *  @param _fincaACompletar Finca existente sin AOI/lotes a completar, o
   *   `null` para el alta normal (crea una finca nueva).
   *  @param _onCerrado Se cerró el modal (por cualquier motivo) — el llamador
   *   refresca el recordatorio del registro en curso.
   *  @param _fincaAEditar Finca YA COMPLETA a reorganizar ("Editar finca", en
   *   el menú de módulos). No se mezcla con `_fincaACompletar`: son opuestos
   *   —aquella no tiene área todavía, ésta sí— y cada uno arranca por su lado
   *   (ver `afterInit`). */
  constructor(
    private readonly _clienteId: string,
    private readonly _maximoHectareas: number | null,
    private readonly _onFincaCreated: (finca: ClienteFincaBaseResponse) => void,
    private readonly _fincaACompletar: ClienteFincaBaseResponse | null = null,
    private readonly _onCerrado: (() => void) | null = null,
    private readonly _fincaAEditar: ClienteFincaBaseResponse | null = null,
  ) {
    super();
  }

  protected getHtml(): string {
    return mustache.render(template, {
      title: "Registro de finca",
      subtitle: "",
    });
  }

  protected afterInit(element: HTMLDivElement): void {
    this._overlay = element.querySelector<HTMLDivElement>(".sx-modal-overlay");
    this._closeBtn = element.querySelector<HTMLElement>('[data-act="close"]');
    this._subtitleEl = element.querySelector<HTMLElement>(
      "#registroFincaSubtitle",
    );
    this._pasoContentEl = element.querySelector<HTMLDivElement>(
      "#registroFincaPasoContent",
    );
    this._progressChipEl = element.querySelector<HTMLElement>(
      "#registroFincaProgressChip",
    );
    this._hectareasBadgeEl = element.querySelector<HTMLElement>(
      "#registroFincaHectareasBadge",
    );
    this._helpBtn = element.querySelector<HTMLButtonElement>(
      "#registroFincaHelpBtn",
    );
    this._instructionsPanelEl = element.querySelector<HTMLDivElement>(
      "#registroFincaInstructionsPanel",
    );
    this._volverBtn = element.querySelector<HTMLButtonElement>(
      "#registroFincaVolver",
    );
    this._submitBtn = element.querySelector<HTMLButtonElement>(
      "#registroFincaSubmit",
    );

    this._closeBtn?.addEventListener("click", this._onCloseClick);
    this._helpBtn?.addEventListener("click", this._onToggleInstructions);
    requestAnimationFrame(() =>
      this._overlay?.classList.add("sx-modal-overlay--open"),
    );

    if (this._pasoContentEl) {
      this._pasoContentEl.innerHTML =
        '<div class="crear-finca-page"><span class="crear-finca-subtitle">Cargando registro…</span></div>';
    }

    // Modo edición: el registro NO se busca, se pide sembrado a partir de la
    // finca (el servidor copia su área y sus lotes y lo deja en paso 3
    // confirmado). El rpc es idempotente, así que volver a entrar retoma el
    // que ya estaba con los cortes hechos. Si falla —típicamente porque hay
    // otro registro en curso— se avisa y se cierra: no tiene sentido caer en
    // el alta de una finca nueva, que es otra cosa de la que pidió el usuario.
    const cargar = this._fincaAEditar
      ? registroFincaTempGrpcService.iniciarEdicionAsync(
          this._fincaAEditar.id,
          this._clienteId,
        )
      : registroFincaTempGrpcService.obtenerEnCursoAsync(this._clienteId);

    cargar
      .then((enCurso) => this._arrancar(enCurso))
      .catch((error) => {
        serviceProvider
          .getErrorService()
          .addError(
            this._fincaAEditar
              ? "Error al abrir la edición de la finca"
              : "Error al cargar el registro de finca",
            parseWizardErrorMessage(error),
          );
        if (this._fincaAEditar) {
          this.dispose();
          return;
        }
        this._arrancar(null);
      });
  }

  /** Retoma el registro en curso o, si no hay, arranca uno nuevo: en paso 2
   *  para completar una finca existente (nombre/tipo ya conocidos; el
   *  registro se crea al confirmar el paso 2), en paso 1 para un alta. */
  private _arrancar(leido: RegistroTemporalLeido | null): void {
    if (this._disposed) return;

    // Solo la identidad del registro: cada paso lee de la base lo que necesita.
    const enCurso = leido?.borrador ?? null;
    if (enCurso) {
      this._setSubtitle(enCurso.nombre ?? "");
      this._irAPaso(enCurso, fx_pasoParaRetomar(enCurso));
      return;
    }

    if (this._fincaACompletar) {
      const borrador = fx_createRegistroFincaBorradorParaCompletar(
        this._fincaACompletar,
      );
      this._setSubtitle(borrador.nombre ?? "");
      this._mount(this._paso2(borrador), 2);
      return;
    }

    const borrador = fx_createRegistroFincaBorradorVacio(this._clienteId);
    this._mount(this._paso1(borrador), 1);
  }

  private _irAPaso(borrador: RegistroFincaBorrador, paso: number): void {
    switch (paso) {
      case 1:
        this._mount(this._paso1(borrador), 1);
        return;
      case 2:
        this._mount(this._paso2(borrador), 2);
        return;
      case 3:
        this._mount(this._paso3(borrador), 3);
        return;
      case 4:
        this._mount(this._paso4(borrador), 4);
        return;
      case 5:
        this._mount(this._paso5(borrador), 5);
        return;
      default:
        this._mount(this._paso6(borrador), 6);
    }
  }

  protected beforeDispose(_element: HTMLDivElement): void {
    this._closeBtn?.removeEventListener("click", this._onCloseClick);
    this._helpBtn?.removeEventListener("click", this._onToggleInstructions);
    this._pasoActual?.disposeBody();
    this._instructionsContent?.dispose();
    this._onCerrado?.();
  }

  private _overlay: HTMLDivElement | null = null;
  private _closeBtn: HTMLElement | null = null;
  private _subtitleEl: HTMLElement | null = null;
  private _pasoContentEl: HTMLDivElement | null = null;
  private _progressChipEl: HTMLElement | null = null;
  private _hectareasBadgeEl: HTMLElement | null = null;
  private _helpBtn: HTMLButtonElement | null = null;
  private _instructionsPanelEl: HTMLDivElement | null = null;
  private _volverBtn: HTMLButtonElement | null = null;
  private _submitBtn: HTMLButtonElement | null = null;
  private _pasoActual: BaseRegistroFincaPasoRender | null = null;
  private _instructionsContent: IConnectRender | null = null;

  /** El botón de cerrar no cierra directo: confirma primero. Lo confirmado
   *  queda en el registro temporal del servidor, así que el mensaje lo dice:
   *  se retoma desde el menú de fincas. Lo que no se llegó a confirmar en el
   *  paso actual (ej. un dibujo sin "Continuar") sí se pierde. */
  private _onCloseClick = (): void => {
    new ConfirmModalRender({
      title: "Cerrar registro",
      message:
        "¿Cerrar el registro de la finca? Lo que ya confirmaste queda guardado y podrás retomarlo desde el menú de fincas.",
      confirmLabel: "Cerrar",
      onConfirm: async () => this.dispose(),
    }).init();
  };

  private _onToggleInstructions = (): void => {
    this._instructionsPanelEl?.classList.toggle("open");
  };

  private _paso1(borrador: RegistroFincaBorrador): RegistroFincaPaso1Render {
    return new RegistroFincaPaso1Render(
      borrador,
      (borradorActualizado) => {
        this._setSubtitle(borradorActualizado.nombre ?? "");
        this._mount(this._paso2(borradorActualizado), 2);
      },
    );
  }

  private _paso2(borrador: RegistroFincaBorrador): RegistroFincaPaso2Render {
    return new RegistroFincaPaso2Render(
      borrador,
      () => this._mount(this._paso1(borrador), 1),
      (importados) => {
        this._mount(this._paso3(borrador, importados), 3);
      },
    );
  }

  private _paso3(
    borrador: RegistroFincaBorrador,
    importados: PoligonoConNombre[] = [],
  ): RegistroFincaPaso3Render {
    return new RegistroFincaPaso3Render(
      borrador,
      importados,
      this._maximoHectareas,
      (text, isError) => this._setHectareasBadge(text, isError),
      () => this._mount(this._paso2(borrador), 2),
      (borradorActualizado) => this._mount(this._paso4(borradorActualizado), 4),
    );
  }

  private _paso4(
    borrador: RegistroFincaBorrador,
  ): RegistroFincaPaso4Render {
    return new RegistroFincaPaso4Render(
      borrador,
      () => this._mount(this._paso3(borrador), 3),
      (borradorActualizado) => this._mount(this._paso5(borradorActualizado), 5),
    );
  }

  private _paso5(borrador: RegistroFincaBorrador): RegistroFincaPaso5Render {
    return new RegistroFincaPaso5Render(
      borrador,
      () => this._mount(this._paso4(borrador), 4),
      (borradorActualizado) => this._mount(this._paso6(borradorActualizado), 6),
    );
  }

  private _paso6(borrador: RegistroFincaBorrador): RegistroFincaPaso6Render {
    return new RegistroFincaPaso6Render(
      borrador,
      () => this._mount(this._paso5(borrador), 5),
      (finca) => {
        this._onFincaCreated(finca);
        this.dispose();
      },
      (paso) => {
        // Una edición no vuelve a los pasos 1–3 ni siquiera para corregir un
        // error del guardado: ahí no hay nada que tocar (nombre, tipo y área
        // son los de la finca). El aviso del error ya se mostró; el usuario se
        // queda en el 6, y lo que sí puede corregir —los lotes— está en el 4.
        const destino = borrador.esEdicion && paso !== 4 ? null : paso;
        if (destino === 1) this._mount(this._paso1(borrador), 1);
        else if (destino === 3) this._mount(this._paso3(borrador), 3);
        else if (destino === 4) this._mount(this._paso4(borrador), 4);
      },
    );
  }

  /** Reemplaza el paso montado: dispone el anterior, pinta body + footer +
   *  avance del nuevo, lo conecta y refresca el panel de indicaciones (si el
   *  paso no declara `getInstructions`, el botón de ayuda se oculta). */
  private _mount(paso: BaseRegistroFincaPasoRender, pasoNumero: number): void {
    this._pasoActual?.disposeBody();
    this._instructionsContent?.dispose();

    // Solo paso 3 lo repuebla (vía el callback que le pasa `_paso3`) — se
    // resetea acá para no dejarlo mostrando un valor viejo en otro paso.
    this._setHectareasBadge(null);

    if (this._pasoContentEl) this._pasoContentEl.innerHTML = paso.renderBody();
    this._updateFooter(paso.getFooterOptions());
    paso.connectBody();
    this._pasoActual = paso;

    if (this._progressChipEl) {
      this._progressChipEl.textContent = `${pasoNumero} / ${TOTAL_PASOS}`;
    }

    this._instructionsPanelEl?.classList.remove("open");
    this._instructionsContent = paso.getInstructions();
    if (this._instructionsPanelEl) {
      this._instructionsPanelEl.innerHTML = this._instructionsContent?.render() ?? "";
    }
    this._instructionsContent?.connect();
    // Clase en vez de `style.display`: así el CSS puede seguir ocultando el
    // botón desde 1024px (el panel queda fijo ahí, no hay nada que
    // togglear) sin pelear con un display inline puesto desde JS.
    this._helpBtn?.classList.toggle(
      "registro-finca-help-hidden",
      !this._instructionsContent,
    );
  }

  /** Muestra/oculta y recablea los 2 botones de avance según lo que declare
   *  el paso actual. Se llaman "footer" por historia (`getFooterOptions`):
   *  hoy viven en la BARRA SUPERIOR, el modal ya no tiene footer. */
  private _updateFooter(options: RegistroFincaFooterOptions): void {
    this._applyFooterOption(this._volverBtn, options.volver);
    this._applyFooterOption(this._submitBtn, options.submit);
  }

  private _applyFooterOption(
    btn: HTMLButtonElement | null,
    option: RegistroFincaFooterOption | null | undefined,
  ): void {
    if (!btn) return;

    if (!option) {
      btn.style.display = "none";
      btn.onclick = null;
      return;
    }

    btn.style.display = "";
    btn.disabled = !!option.disabled;
    if (option.label) btn.textContent = option.label;
    btn.onclick = option.onClick;
  }

  private _setSubtitle(nombreFinca: string): void {
    if (this._subtitleEl) this._subtitleEl.textContent = nombreFinca;
  }

  /** Badge de hectáreas disponibles, al lado del avance de pasos —
   *  solo lo usa el paso 3 (`_paso3`), `null` lo oculta. `isError` lo pinta
   *  rojo (`sx-badge-error`, clase global) en vez del neutro de siempre —
   *  paso 3 lo dispara apenas se supera el máximo del plan (independiente
   *  del margen de tolerancia que solo afecta si "Continuar" bloquea o no). */
  private _setHectareasBadge(text: string | null, isError = false): void {
    if (!this._hectareasBadgeEl) return;
    this._hectareasBadgeEl.textContent = text ?? "";
    this._hectareasBadgeEl.style.display = text ? "" : "none";
    this._hectareasBadgeEl.classList.toggle("sx-badge-error", isError);
    this._hectareasBadgeEl.classList.toggle("sx-badge-neutral", !isError);
  }
}
